import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getHealthScoreColor, getHealthStatusInfo } from '../utils/healthColorUtils';

// Color Palettes for Health Systems and Lifestyle Domains
const HEALTH_SYSTEM_COLORS = {
  Neurological: '#FF6347',    // Tomato
  Cardiovascular: '#4682B4', // SteelBlue
  Respiratory: '#3CB371',   // MediumSeaGreen
  Metabolic: '#FFD700',      // Gold
  Filtration: '#DA70D6',     // Orchid
  Musculoskeletal: '#8A2BE2',// BlueViolet
  Immune: '#6495ED',         // CornflowerBlue
};

const LIFESTYLE_DOMAIN_COLORS = {
  Nutrition: '#FFB347', // Pastel Orange
  Movement: '#77DD77',  // Pastel Green
  Recovery: '#AEC6CF',  // Pastel Blue
  'Mental Health': '#CBAACB', // Pastel Purple
  Emotion: '#CBAACB', // Pastel Purple (alternative name)
  Environment: '#F0E68C', // Khaki/Light Yellow
};



const SVGCoxcombChart = ({
  healthSystemsData,
  lifestyleDomainsData,
  title = "Health Overview",
  chartType = "combined",
  systemDeviations = {},
  lifestyleDeviations = {},
  onSegmentClick = null
}) => {
  // State for hover tooltip and interactions (must be at top level)
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Handle screen resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to get the color for a segment based on health score (7-color gradient spectrum)
  const getSegmentColor = (score) => {
    return getHealthScoreColor(score);
  };

  // Prepare data based on chart type
  const prepareChartData = () => {
    let dataToProcess = [];

    if (chartType === "systems" && healthSystemsData && healthSystemsData.length > 0) {
      // Only health systems
      console.log('[SVGCoxcombChart] Processing systems data:', healthSystemsData);
      healthSystemsData.forEach((item, index) => {
        console.log('[SVGCoxcombChart] Processing system item:', item);
        if (item.score !== null && item.score !== undefined && !isNaN(item.score)) {
          const score = Math.max(0, Math.min(100, item.score));
          const processedItem = {
            name: item.name,
            score: Math.round(score),
            type: 'Health System',
            fill: getSegmentColor(Math.round(score)),
            index: index
          };
          console.log('[SVGCoxcombChart] Adding system item:', processedItem);
          dataToProcess.push(processedItem);
        } else {
          console.log('[SVGCoxcombChart] Skipping invalid system item:', item);
        }
      });
    } else if (chartType === "lifestyle") {
      // Only lifestyle domains
      if (lifestyleDomainsData && lifestyleDomainsData.length > 0) {
        console.log('[SVGCoxcombChart] Processing lifestyle data:', lifestyleDomainsData);
        lifestyleDomainsData.forEach((item, index) => {
          console.log('[SVGCoxcombChart] Processing lifestyle item:', item);
          if (item.score !== null && item.score !== undefined && !isNaN(item.score)) {
            const score = Math.max(0, Math.min(100, item.score));
            const processedItem = {
              name: item.name,
              score: Math.round(score),
              type: 'Lifestyle Domain',
              fill: getSegmentColor(Math.round(score)),
              index: index
            };
            console.log('[SVGCoxcombChart] Adding lifestyle item:', processedItem);
            dataToProcess.push(processedItem);
          } else {
            console.log('[SVGCoxcombChart] Skipping invalid lifestyle item:', item);
          }
        });
      }

      // Add Environment placeholder if not already present
      const hasEnvironment = dataToProcess.some(item => item.name.toLowerCase() === 'environment');
      if (!hasEnvironment) {
        dataToProcess.push({
          name: 'Environment',
          score: 0, // Placeholder score
          type: 'Lifestyle Domain',
          fill: getSegmentColor(0),
          index: dataToProcess.length,
          isPlaceholder: true
        });
      }
    } else {
      // Combined (original behavior)
      if (healthSystemsData && healthSystemsData.length > 0) {
        healthSystemsData.forEach((item, index) => {
          if (item.score !== null && item.score !== undefined && !isNaN(item.score) && item.score >= 0) {
            const score = Math.max(0, Math.min(100, item.score));
            dataToProcess.push({
              name: item.name,
              score: Math.round(score),
              type: 'Health System',
              fill: getSegmentColor(Math.round(score)),
              index: index
            });
          }
        });
      }

      if (lifestyleDomainsData && lifestyleDomainsData.length > 0) {
        lifestyleDomainsData.forEach((item, index) => {
          if (item.score !== null && item.score !== undefined && !isNaN(item.score) && item.score >= 0) {
            const score = Math.max(0, Math.min(100, item.score));
            dataToProcess.push({
              name: item.name,
              score: Math.round(score),
              type: 'Lifestyle Domain',
              fill: getSegmentColor(Math.round(score)),
              index: index + (healthSystemsData ? healthSystemsData.length : 0)
            });
          }
        });
      }
    }

    return dataToProcess;
  };

  const chartData = prepareChartData();

  console.log('[SVGCoxcombChart] Final chartData:', chartData);
  console.log('[SVGCoxcombChart] Props:', { healthSystemsData, lifestyleDomainsData, chartType, title });

  if (!chartData || chartData.length === 0) {
    console.warn('[SVGCoxcombChart] No chart data available');
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        No health data available to display chart.
      </div>
    );
  }

  // Responsive chart dimensions based on screen width
  const getResponsiveSize = () => {
    if (screenWidth < 576) return 300; // Mobile phones
    if (screenWidth < 768) return 400; // Small tablets
    if (screenWidth < 992) return 500; // Medium tablets
    return 600; // Desktop and large screens
  };

  const size = getResponsiveSize();
  const center = size / 2;
  const maxRadius = size * 0.33; // Scale radius proportionally
  const minRadius = 5; // Minimized center circle

  // Calculate angles for each segment, preventing division by zero
  const angleStep = chartData.length > 0 ? (2 * Math.PI) / chartData.length : 0;

  // Create SVG paths for each segment (true Coxcomb style)
  const createSegmentPath = (data, index) => {
    const startAngle = index * angleStep - Math.PI / 2; // Start from top
    const endAngle = (index + 1) * angleStep - Math.PI / 2;

    // Calculate radius based on score (0-100 maps to minRadius-maxRadius)
    let radius = minRadius + (data.score / 100) * (maxRadius - minRadius);
    if (isNaN(radius)) {
      radius = minRadius; // Fallback to minimum radius if calculation fails
    }

    // Calculate points for the segment
    const x1 = center + minRadius * Math.cos(startAngle);
    const y1 = center + minRadius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(startAngle);
    const y2 = center + radius * Math.sin(startAngle);
    const x3 = center + radius * Math.cos(endAngle);
    const y3 = center + radius * Math.sin(endAngle);
    const x4 = center + minRadius * Math.cos(endAngle);
    const y4 = center + minRadius * Math.sin(endAngle);

    // Create path for Coxcomb segment
    const largeArcFlag = angleStep > Math.PI ? 1 : 0;

    return `
      M ${x1} ${y1}
      L ${x2} ${y2}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x3} ${y3}
      L ${x4} ${y4}
      A ${minRadius} ${minRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}
      Z
    `;
  };

  const createFullSlicePath = (index) => {
    const startAngle = index * angleStep - Math.PI / 2;
    const endAngle = (index + 1) * angleStep - Math.PI / 2;

    const x1 = center + minRadius * Math.cos(startAngle);
    const y1 = center + minRadius * Math.sin(startAngle);
    const x2 = center + maxRadius * Math.cos(startAngle);
    const y2 = center + maxRadius * Math.sin(startAngle);
    const x3 = center + maxRadius * Math.cos(endAngle);
    const y3 = center + maxRadius * Math.sin(endAngle);
    const x4 = center + minRadius * Math.cos(endAngle);
    const y4 = center + minRadius * Math.sin(endAngle);

    const largeArcFlag = angleStep > Math.PI ? 1 : 0;

    return `
      M ${x1} ${y1}
      L ${x2} ${y2}
      A ${maxRadius} ${maxRadius} 0 ${largeArcFlag} 1 ${x3} ${y3}
      L ${x4} ${y4}
      A ${minRadius} ${minRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}
      Z
    `;
  };

  const handleMouseEnter = (data, event) => {
    setHoveredSegment(data);
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  const handleMouseMove = (event) => {
    if (hoveredSegment) {
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleSegmentClick = (data) => {
    if (onSegmentClick) {
      // Use external handler if provided
      onSegmentClick(data);
    }
    // Removed internal modal functionality
  };

  // Function to get professional medical icons for each segment
  const getProfessionalIcon = (segmentName) => {
    const iconMap = {
      // Health Systems - PNG Image Paths
      'Neurological': '/icons/Neurological icon.png',
      'Cardiovascular': '/icons/Cardiovascular icon.png',
      'Respiratory': '/icons/Respiratory icon.png',
      'Metabolic': '/icons/Metabolic icon.png',
      'Filtration': '/icons/Filtration icon.png',
      'Musculoskeletal': '/icons/Musculoskeletal icon.png',
      'Immune': '/icons/Immune icon.png',

      // Lifestyle Domains - PNG Image Paths
      'Nutrition': '/icons/Nutrition icon.png',
      'Movement': '/icons/Movement icon.png',
      'Recovery': '/icons/Recovery icon.png',
      'Mental Health': '/icons/Emotion icon.png',
      'Emotion': '/icons/Emotion icon.png',
      'Environment': '/icons/Environment icon.png',
    };

    // Return PNG icon path or null if not found (no fallbacks)
    return iconMap[segmentName] || null;
  };

  // Function to render professional PNG icons
  const renderProfessionalIcon = (segmentName, x, y, baseSize = 24, score = 0) => {
    // Make icon size responsive
    const getResponsiveIconSize = () => {
      if (screenWidth < 576) return Math.max(16, baseSize * 0.7); // Smaller on mobile
      if (screenWidth < 768) return Math.max(20, baseSize * 0.85); // Medium on small tablets
      return baseSize; // Full size on larger screens
    };

    const size = getResponsiveIconSize();
    const iconPath = getProfessionalIcon(segmentName);
    const iconColor = getSegmentColor(score);

    // Only render if PNG icon path exists
    if (!iconPath) return null;

    // Render PNG images using foreignObject with img tag
    return (
      <foreignObject
        x={x - size/2}
        y={y - size/2}
        width={size}
        height={size}
        style={{ pointerEvents: 'none' }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}>
          <img
            src={iconPath}
            alt={`${segmentName} icon`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: `drop-shadow(0 2px 4px ${iconColor}40)`
            }}
          />
        </div>
      </foreignObject>
    );
  };

  // Helper function to check if segment is hovered
  const isSegmentHovered = (index) => {
    return hoveredSegment && chartData[index] === hoveredSegment;
  };



  // Get performance level description using the new 7-color system
  const getPerformanceLevel = (score) => {
    return getHealthStatusInfo(score);
  };



  return (
    <div style={{ width: '100%', textAlign: 'center', position: 'relative' }}>
      <h3 style={{
        marginBottom: screenWidth < 576 ? '10px' : '20px',
        color: '#2c3e50',
        fontSize: screenWidth < 576 ? '16px' : '18px'
      }}>{title}</h3>

      <div style={{
        display: 'inline-block',
        position: 'relative',
        maxWidth: '100%',
        overflow: 'visible'
      }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{
            overflow: 'visible',
            maxWidth: '100%',
            height: 'auto'
          }}
          onMouseMove={handleMouseMove}
        >


          {/* Outer boundary circle */}
          <circle
            cx={center}
            cy={center}
            r={maxRadius}
            fill="none"
            stroke="#d0d0d0"
            strokeWidth="2"
          />

          {/* Segments */}
          {chartData.map((data, index) => {
            const isHovered = isSegmentHovered(index);
            const performance = getPerformanceLevel(data.score);

            return (
              <g key={index} onClick={() => handleSegmentClick(data)}>
                <path
                  d={createFullSlicePath(index)}
                  fill="transparent"
                  stroke="none"
                  style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
                  onMouseEnter={(e) => handleMouseEnter(data, e)}
                  onMouseLeave={handleMouseLeave}
                />
                <path
                  d={createSegmentPath(data, index)}
                  fill={data.fill}
                  stroke={isHovered ? "#333" : "#fff"}
                  strokeWidth={isHovered ? "3" : "2"}
                  opacity={data.isPlaceholder ? (isHovered ? "0.6" : "0.4") : (isHovered ? "0.95" : "0.85")}
                  style={{
                    transition: 'all 0.3s ease',
                    filter: isHovered ? 'brightness(1.1) drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
                    pointerEvents: 'none'
                  }}
                />
              </g>
            );
          })}

          {/* Division lines between segments */}
          {chartData.map((data, index) => {
            const angle = index * angleStep - Math.PI / 2;
            const x1 = center + minRadius * Math.cos(angle);
            const y1 = center + minRadius * Math.sin(angle);
            const x2 = center + maxRadius * Math.cos(angle);
            const y2 = center + maxRadius * Math.sin(angle);

            return (
              <line
                key={`divider-${index}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#333"
                strokeWidth="1.5"
                opacity="0.3"
                style={{ pointerEvents: 'none' }}
              />
            );
          })}

          {/* Professional medical icons positioned in the center of each segment */}
          {chartData.map((data, index) => {
            const angle = index * angleStep + angleStep / 2 - Math.PI / 2;
            const iconRadius = minRadius + (maxRadius - minRadius) * 0.7; // Position icons at 70% of the segment
            const x = center + iconRadius * Math.cos(angle);
            const y = center + iconRadius * Math.sin(angle);

            // Responsive font size for score text
            const getResponsiveFontSize = () => {
              if (screenWidth < 576) return "10";
              if (screenWidth < 768) return "12";
              return "14";
            };

            // Responsive icon size
            const getResponsiveIconSize = () => {
              if (screenWidth < 576) return 20;
              if (screenWidth < 768) return 26;
              return 32;
            };

            return (
              <g key={`icon-${index}`}>
                {/* Professional Medical Icon */}
                {renderProfessionalIcon(data.name, x, y, getResponsiveIconSize(), data.score)}

                {/* Score percentage below the icon */}
                <text
                  x={x}
                  y={y + (getResponsiveIconSize() * 0.875)} // Adjust text position based on icon size
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={getResponsiveFontSize()}
                  fill="#333"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {data.isPlaceholder ? '...' : `${data.score}%`}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Custom Tooltip */}
        {hoveredSegment && (
          <div
            style={{
              position: 'fixed',
              left: mousePosition.x + 10,
              top: mousePosition.y - 10,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '13px',
              pointerEvents: 'none',
              zIndex: 1000,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <div style={{
              fontSize: '18px',
              color: getSegmentColor(hoveredSegment.score),
              display: 'flex',
              alignItems: 'center'
            }}>
              {(() => {
                const iconPath = getProfessionalIcon(hoveredSegment.name);
                if (iconPath) {
                  return <img src={iconPath} alt={`${hoveredSegment.name} icon`} style={{ width: '18px', height: '18px', objectFit: 'contain' }} />;
                }
                return null;
              })()}
            </div>
            <span>
              {hoveredSegment.isPlaceholder
                ? `${hoveredSegment.name}: Coming Soon`
                : `${hoveredSegment.name}: ${hoveredSegment.score}%`
              }
            </span>
          </div>
        )}
      </div>

      {/* Interactive Controls and Information */}
      <div style={{
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '500px',
        margin: '20px auto 0'
      }}>




      </div>
    </div>
  );
};

SVGCoxcombChart.propTypes = {
  healthSystemsData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      score: PropTypes.number.isRequired,
    })
  ),
  lifestyleDomainsData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      score: PropTypes.number.isRequired,
    })
  ),
  title: PropTypes.string,
  chartType: PropTypes.oneOf(['combined', 'systems', 'lifestyle']),
  systemDeviations: PropTypes.object,
  lifestyleDeviations: PropTypes.object,
  onSegmentClick: PropTypes.func,
};

SVGCoxcombChart.defaultProps = {
  healthSystemsData: [],
  lifestyleDomainsData: [],
  title: "Health Overview",
  chartType: "combined",
};

export default SVGCoxcombChart;
